import { useEffect, useMemo } from 'react';
import type { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Spinner } from '@trinity/chat-components/components/presentation/Spinner.js';
import * as $salesforce from '@/auth/salesforce';
import { actions } from '@/store/actions';

type PageError = {
  error: string;
  description: string;
};

export function SalesforceAuthCallback() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const signedRequest = searchParams.get('signed_request');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  const queryError = useMemo<PageError | null>(() => {
    if (error) {
      return {
        error,
        description: errorDescription ?? 'Unknown error',
      };
    }

    if (!signedRequest) {
      return {
        error: 'invalid_request',
        description: 'Missing Salesforce signed request.',
      };
    }

    return null;
  }, [error, errorDescription, signedRequest]);

  const pageError = queryError;

  useEffect(() => {
    if (queryError || !signedRequest) {
      return;
    }

    let isActive = true;

    $salesforce
      .callback({
        signedRequest,
      })
      .then((data) => {
        if (data.type === 'redirect') {
          dispatch(actions.authChange());
          navigate(data.returnUrl ? data.returnUrl : '/home', { replace: true });
        }
      })
      .catch((err: unknown) => {
        if (!isActive) {
          return;
        }

        const errorResponse = err as AxiosError<{ message?: string }>;
        const params = new URLSearchParams();
        params.set('error', errorResponse.code ?? 'salesforce_auth_failed');
        params.set('error_description', errorResponse.response?.data?.message ?? errorResponse.message);

        navigate(
          {
            pathname: location.pathname,
            search: `?${params.toString()}`,
          },
          {
            replace: true,
          }
        );
      });

    return () => {
      isActive = false;
    };
  }, [dispatch, location.pathname, navigate, queryError, signedRequest]);

  return (
    <TopRoot>
      <Root>
        <Body>
          {pageError ? (
            <ErrorWrapper>
              <ErrorHeader>{`We're sorry, something went wrong.`}</ErrorHeader>
              <ErrorLabel>Code:</ErrorLabel>
              <ErrorLine>{pageError.error || 'N/A'}</ErrorLine>
              <ErrorLabel>Description:</ErrorLabel>
              <ErrorLine>{pageError.description || 'N/A'}</ErrorLine>
            </ErrorWrapper>
          ) : (
            <Spinner />
          )}
        </Body>
      </Root>
    </TopRoot>
  );
}

const TopRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '80vh',
});

const Root = styled.div({
  borderRadius: '10px',
  backgroundColor: 'var(--gray-xxl)',
  padding: '32px',
  minWidth: '400px',
  maxWidth: '800px',
});

const Body = styled.div({
  marginTop: '20px',
});

const ErrorWrapper = styled.div({
  color: 'var(--black)',
  backgroundColor: 'var(--reddish-white)',
  borderRadius: '15px',
  padding: '32px',
  fontSize: '16px',
});

const ErrorHeader = styled.h1({
  fontSize: '20px',
  fontFamily: 'var(--font-bold)',
  lineHeight: '28px',
  margin: '0 0 30px 0',
});

const ErrorLabel = styled.div({
  fontSize: '16px',
  fontFamily: 'var(--font-semibold)',
  margin: '0 0 4px 0',
});

const ErrorLine = styled.div({
  fontSize: '16px',
  lineHeight: '18px',

  ':not(:last-child)': {
    margin: '0 0 20px 0',
  },
});
